import React, { useState } from 'react';
import { useKmClient } from '../../../lib/useKmClient.js';
import { useKmQuery } from '../hooks/useKmQuery.js';
import { useFiltriEsperienze } from '../hooks/useTassonomie.js';
import ErrorDetails from '../components/ErrorDetails.jsx';
import ModelliLlmPanel from '../components/ModelliLlmPanel.jsx';
import { formatDataIt } from '../preavvisi.js';

/* 04/11 — Dashboard KM dell'AF v2.3 §9.8 (UC-KM-05, RF-KM-29…31).
   GET /indicatori rende i 13 indicatori del catalogo (KPI-A1…S3) con
   dimensione, valore, periodo precedente, soglia e motivo quando il dato
   non è disponibile. Senza filtri sono i valori dell'ultimo ricalcolo
   giornaliero; periodo, settore e dominio ricalcolano la vista.

   V41: sotto le 5 risorse il backend non rende il valore. Qui si mostra
   «dato non disponibile» con il motivo, mai uno zero. Accesso: Management
   e HUB HR (il backend risponde 403 agli altri ruoli). */

const DIMENSIONI = ['Adozione', 'Completezza', 'Valore commerciale', 'Soddisfazione'];
const PERIODI = [30, 90, 180, 365];

function formatta(valore, unita) {
  if (valore === null || valore === undefined) return null;
  const n = Number(valore).toLocaleString('it-IT', { maximumFractionDigits: 2 });
  return unita === '%' ? `${n}%` : `${n} ${unita}`;
}

function Variazione({ corrente, precedente, unita }) {
  if (corrente === null || precedente === null || precedente === undefined) {
    return <span>periodo precedente: {formatta(precedente, unita) || 'n.d.'}</span>;
  }
  const delta = Number(corrente) - Number(precedente);
  const segno = delta > 0 ? '+' : '';
  return (
    <span>
      periodo precedente: {formatta(precedente, unita)} ({segno}{delta.toLocaleString('it-IT', { maximumFractionDigits: 2 })}{unita === '%' ? ' pt' : ''})
    </span>
  );
}

function Dettaglio({ kpi, nomeArea }) {
  const d = kpi.dettaglio;
  if (!d) return null;
  if (kpi.id === 'KPI-A4' && Array.isArray(d)) {
    return (
      <table style={{ width: '100%', fontSize: 'var(--font-size-xs)', marginTop: 'var(--space-2)' }}>
        <tbody>
          {d.map((a) => (
            <tr key={`${a.asse}-${a.codice}`}>
              <td>{a.asse === 'settore' ? 'Settore' : 'Dominio'} {nomeArea(a.asse, a.codice)}</td>
              <td style={{ textAlign: 'right' }}>
                {a.valore === null ? 'n.d. (V41)' : `${a.valore}%`}{a.valore === 0 ? ' · scoperta' : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (kpi.id === 'KPI-S1') {
    return <div style={{ fontSize: 'var(--font-size-xs)' }}>{Object.entries(d).map(([p, m]) => `${p}: ${m} min`).join(' · ')}</div>;
  }
  if (kpi.id === 'KPI-A2') {
    return <div style={{ fontSize: 'var(--font-size-xs)' }}>compilati {d.compilati} · scaduti {d.scaduti_pct ?? 'n.d.'}%</div>;
  }
  if (kpi.id === 'KPI-V2') {
    return <div style={{ fontSize: 'var(--font-size-xs)' }}>schede consultate: {d.consultate}</div>;
  }
  return null;
}

function Andamento({ metrica, unita }) {
  const kmClient = useKmClient('km');
  const { data, status, error } = useKmQuery(
    () => kmClient.get(`/indicatori/${metrica}/andamento`, { query: { giorni: 180 } }),
    [metrica]
  );
  if (status === 'loading') return <div className="skeleton-line" style={{ width: '60%' }} />;
  if (status === 'error') return <ErrorDetails error={error} />;
  const serie = Array.isArray(data?.serie) ? data.serie.slice(-14) : [];
  if (!serie.length) return <div style={{ fontSize: 'var(--font-size-xs)' }}>Nessun ricalcolo ancora registrato.</div>;
  return (
    <table style={{ width: '100%', fontSize: 'var(--font-size-xs)', marginTop: 'var(--space-2)' }}>
      <tbody>
        {serie.map((p) => (
          <tr key={p.periodo}>
            <td>{formatDataIt(p.periodo)}</td>
            <td style={{ textAlign: 'right' }}>{formatta(p.valore, unita) || 'n.d.'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function DashboardPage() {
  const kmClient = useKmClient('km');
  const { filtri } = useFiltriEsperienze();
  const [giorni, setGiorni] = useState(90);
  const [settore, setSettore] = useState('');
  const [dominio, setDominio] = useState('');
  const [aperto, setAperto] = useState(null);

  const { data, status, error, refetch } = useKmQuery(
    () => kmClient.get('/indicatori', {
      query: { giorni, settore_l1: settore || undefined, dominio_l1: dominio || undefined },
    }),
    [giorni, settore, dominio]
  );

  const indicatori = Array.isArray(data?.indicatori) ? data.indicatori : [];
  const nomeArea = (asse, codice) => {
    const elenco = asse === 'settore' ? filtri?.settori : filtri?.domini;
    return (elenco || []).find((x) => x.codice === codice)?.nome || codice;
  };
  const sel = { className: 'field-select', style: { maxWidth: 220 } };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
        <h1 className="section-title" style={{ margin: 0 }}>Dashboard</h1>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'center', fontSize: 'var(--font-size-sm)' }}>
          <select {...sel} value={giorni} onChange={(e) => setGiorni(Number(e.target.value))}>
            {PERIODI.map((n) => <option key={n} value={n}>Ultimi {n} giorni</option>)}
          </select>
          <select {...sel} value={settore} onChange={(e) => setSettore(e.target.value)}>
            <option value="">Settore L1 (tutti)</option>
            {(filtri?.settori || []).map((x) => <option key={x.codice} value={x.codice}>{x.nome}</option>)}
          </select>
          <select {...sel} value={dominio} onChange={(e) => setDominio(e.target.value)}>
            <option value="">Dominio L1 (tutti)</option>
            {(filtri?.domini || []).map((x) => <option key={x.codice} value={x.codice}>{x.nome}</option>)}
          </select>
        </div>
      </div>

      <ModelliLlmPanel />

      {status === 'success' && (
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)', marginBottom: 'var(--space-5)' }}>
          Periodo {formatDataIt(data.periodo?.da)} – {formatDataIt(data.periodo?.a)}
          {data.aggiornato_il ? ` · aggiornamento del ${new Date(data.aggiornato_il).toLocaleString('it-IT')}` : ''}
          {` · valori aggregati, non esposti sotto le ${data.min_risorse ?? 5} risorse (V41)`}
        </p>
      )}

      {status === 'loading' && (
        <div className="card"><div className="skeleton-line" style={{ width: '50%' }} /></div>
      )}

      {status === 'error' && (
        <div className="state-error">
          Non è stato possibile caricare gli indicatori.
          <div><button type="button" className="btn-link" onClick={refetch}>Riprova</button></div>
          <ErrorDetails error={error} />
        </div>
      )}

      {status === 'success' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          {DIMENSIONI.map((dimensione) => (
            <div key={dimensione} className="card">
              <h2 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>{dimensione}</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 'var(--space-4)' }}>
                {indicatori.filter((k) => k.dimensione === dimensione).map((k) => {
                  const valore = formatta(k.valore, k.unita);
                  return (
                    <div key={k.id} style={{ borderLeft: `3px solid ${k.soglia_superata ? 'var(--color-danger)' : 'var(--color-border)'}`, paddingLeft: 'var(--space-3)' }}>
                      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }} title={k.descrizione}>{k.nome}</div>
                      <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700 }}>
                        {valore || <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 400, color: 'var(--color-ink-muted)' }}>dato non disponibile</span>}
                      </div>
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)' }}>
                        {valore ? <Variazione corrente={k.valore} precedente={k.valore_precedente} unita={k.unita} /> : k.motivo}
                      </div>
                      {k.soglia_superata && (
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}>
                          oltre la soglia di attenzione ({k.verso_soglia === 'min' ? 'minimo' : 'massimo'} {formatta(k.soglia, k.unita)})
                        </div>
                      )}
                      {valore && <Dettaglio kpi={k} nomeArea={nomeArea} />}
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-faint)' }}>
                        {k.id} · {k.rilascio}
                        {' · '}
                        <button type="button" className="btn-link" onClick={() => setAperto(aperto === k.id ? null : k.id)}>
                          {aperto === k.id ? 'chiudi andamento' : 'andamento'}
                        </button>
                      </div>
                      {aperto === k.id && <Andamento metrica={k.id} unita={k.unita} />}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
